import { useApolloClient } from '@apollo/client'
import { useRouter } from 'next/router'
import { useMemo, useState } from 'react'

import { NotificationType, notify } from 'src/providers/NotificationProvider'

import { LoadingSpinner } from 'src/components/atoms/LoadingSpinner'

import {
  GetAdminPlantingQuery,
  useGetAdminPlantingQuery
} from 'src/config/generated/graphql'
import { NotFound } from 'src/pages/404/404'
import { MappedPhotos, mapPhotos } from 'src/utils/assets'

import { PlantingPhotos } from '../PlantingPhotos'
import { AdminPlantingNav } from '../navs/AdminPlantingNav/AdminPlantingNav'
import { AdminPlantingNavTab } from '../navs/AdminPlantingNav/helper'

const mapAssets = (
  planting: NonNullable<GetAdminPlantingQuery['Planting']>
) => {
  return {
    name: planting.Name,
    plantingAssets: planting.PlantingAssets.map(pa => ({
      id: pa.Id,
      uri: pa.Uri!
    })),
    monitoringEvents: planting.MonitoringEvents.map(me => ({
      monitoredOn: me.MonitoredOn,
      monitoringAssets: me.MonitoringAssets.map(ma => ({
        id: ma.Id,
        uri: ma.Uri!
      }))
    }))
  }
}

export const AdminViewPlantingPhotos = () => {
  const router = useRouter()
  const client = useApolloClient()
  const [photos, setPhotos] = useState<MappedPhotos>()
  const { plantingId } = router.query as { plantingId: string }

  const { data: plantingData, loading: isPlantingLoading } =
    useGetAdminPlantingQuery({
      fetchPolicy: 'cache-and-network',
      variables: {
        id: plantingId
      },
      onCompleted: async response => {
        try {
          if (!response || !response.Planting) {
            setPhotos(undefined)
            return
          }
          const plantingAssets = mapAssets(response.Planting)
          const photoFiles = await mapPhotos(client, [plantingAssets])

          setPhotos(photoFiles)
        } catch (error: any) {
          notify({
            message: 'Could not load planting photos',
            type: NotificationType.error
          })
          setPhotos(undefined)
        }
      }
    })

  const planting = useMemo(
    () => plantingData?.Planting,
    [plantingData?.Planting]
  )

  if (!planting && !isPlantingLoading) return <NotFound />

  return (
    <LoadingSpinner isLoading={isPlantingLoading}>
      <AdminPlantingNav
        activeTab={AdminPlantingNavTab.Photos}
        planting={planting!}
      >
        <PlantingPhotos photos={photos} />
      </AdminPlantingNav>
    </LoadingSpinner>
  )
}
